'use client';

import React, { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * "App of the Day" + 4.9 star rating (Figma 303:3239).
 *
 * Two placements, one strip: desktop keeps the original spot inside the hero copy column
 * (below the CTA, left-aligned — rendered by HeroSection via MediaBadgesStrip), while
 * mobile shows it as its own section below the story banner (the default export).
 *
 * "App of the Day" stays a single self-contained media-block asset (laurels baked in).
 * The rating is inlined as SVG rather than an <img> so the five stars can be animated
 * individually — they fill in one at a time the first time the strip comes into view, at
 * both breakpoints.
 */

// One star, positioned by the x of its top tip inside the 153x32 artboard (Figma).
const star = (x: number) => `M${x} 9.2l2 4h.1l4.4.6v.2l-3.1 3v.1l.7 4.4q0 0-.2 0l-3.9-2-4 2 .7-4.4-3.2-3.1v-.2l4.4-.6z`;

const STAR_XS = [31, 47, 63, 79, 95];

// Memoize star paths to avoid recalculation on re-renders
const STAR_PATHS = STAR_XS.map(x => star(x));

// Fades the whole strip up on mobile; the stars then stagger themselves via `delay`
// below. The hero opts out of this entirely — there, only the stars move.
const STRIP_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  shown: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
  },
};

const IN_VIEW_ONCE = { once: true, amount: 0.6 } as const;

/** Mobile-only section below the story banner. Desktop renders the strip in the hero. */
export default function MediaBadges() {
  return (
    <section className="w-full pt-[36px] pb-[10px] dt:hidden">
      <div className="hp-container">
        <MediaBadgesStrip animateStars revealStrip />
      </div>
    </section>
  );
}

/**
 * The badge pair itself. `className` is appended, so callers can override the default
 * centring (the hero left-aligns it) without touching the sizing.
 *
 * Two independent switches:
 *  - `animateStars` — the one-by-one star fill.
 *  - `revealStrip`  — fades the whole pair up from 12px on entry. Mobile uses it; the
 *    hero doesn't, so "App of the Day" and the laurels are simply there from the start
 *    and only the stars animate.
 */
export function MediaBadgesStrip({
  className = '',
  animateStars = false,
  revealStrip = false,
}: {
  className?: string;
  animateStars?: boolean;
  revealStrip?: boolean;
}) {
  const reduceMotion = useReducedMotion() ?? false;
  const stars = animateStars && !reduceMotion;
  const reveal = revealStrip && !reduceMotion;

  const strip = (
    <>
      <img
        src="/images/comix/homepage/v2/media-block-app-of-the-day.svg"
        alt="App of the Day"
        width={155}
        height={32}
        // min-w-0 is load-bearing: without it a flex-1 item keeps `min-width: auto`,
        // which for this <img> resolves to its 155px intrinsic width and stops it
        // shrinking — so on a narrow viewport it hogs the row and the sibling <svg>
        // (min-width 0, no width attr) gets squeezed smaller. Samsung Internet showed
        // this as two mismatched badge heights. min-w-0 on both lets them split 50/50.
        className="w-full max-w-[155px] min-w-0 flex-1"
        style={{ aspectRatio: '155 / 32' }}
      />
      {/* Without a motion parent the SVG has to own the in-view trigger itself. */}
      <RatingBadge animated={stars} ownsTrigger={!reveal} />
    </>
  );

  const classes = `mx-auto flex w-full max-w-[322px] items-center justify-center gap-4 ${className}`;

  if (!reveal) return <div className={classes}>{strip}</div>;

  return (
    <motion.div
      className={classes}
      variants={STRIP_VARIANTS}
      initial="hidden"
      whileInView="shown"
      // once: this is a first-appearance flourish, not a scroll-linked effect.
      viewport={{ once: true, amount: 0.6 }}
    >
      {strip}
    </motion.div>
  );
}

function RatingBadge({ animated, ownsTrigger }: { animated: boolean; ownsTrigger: boolean }) {
  // motion.svg with no animation props renders a plain <svg>, so this is safe either way.
  return (
    <motion.svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 153 32"
      fill="none"
      role="img"
      aria-label="Rated 4.9 out of 5 stars"
      // min-w-0 is the fix here: a `flex-1` item keeps `min-width: auto`, which for the
      // sibling <img> resolves to its 155px intrinsic width and stops it shrinking — so on
      // a narrow viewport it hogs the row and this <svg> gets squeezed shorter (Samsung
      // Internet showed two mismatched badge heights). min-w-0 on both lets them split
      // 50/50; the viewBox + aspect-ratio then keep the height in step with the width.
      // Do NOT add width/height attrs here — a fixed `height` attr pins the SVG to 32px and
      // stops it scaling down with the shared flex width.
      className="w-full max-w-[153px] min-w-0 flex-1"
      style={{ aspectRatio: '153 / 32' }}
      {...(animated && ownsTrigger
        ? { initial: 'hidden' as const, whileInView: 'shown' as const, viewport: IN_VIEW_ONCE }
        : {})}
    >
      <g fill="#111" clipPath="url(#mb-laurel-l)"><path d="M3.8 18.7h-.4v.2l.1.4.3.6q.6 1.2 1.8 1.7l.5.2 1 .1h.6v-.4l-.3-.7v-.2l-.2-.2A4 4 0 0 0 5 18.7zM13.3.5l-.3.1-.7.4a4 4 0 0 0-1.9 2.3V5l.5-.1.6-.3.8-.6.4-.5.5-1 .2-.6v-.3l.1-1q0-.1-.2 0M14.4 23.8h-.2v.1l-.7.7-.5 1-.2 1.2q0 1 .4 1.7l.4.5h.1l.1-.1a4 4 0 0 0 1.2-2.1 5 5 0 0 0-.3-2.6V24zM4.2 7.2v.4l-.1 1v.9l.4 1 .5.8q.6.7 1.2 1h.1l.2-.7v-.2l.1-.7v-.8A4 4 0 0 0 5.4 8L5 7.6l-.4-.2zM7.2 2.4l-.2.2v.2q-.5.7-.5 1.6v1Q6.9 7 7.9 7.8L8 8l.2-.2v-.1a4 4 0 0 0-.6-4.8l-.1-.1zM12.7 6h-.9A3 3 0 0 0 10 7q-.6.5-1 1.4v.1l.4.1q1.7.2 3-.6a5 5 0 0 0 1.3-1.6V6h-.9M5 24v.3l.1.1v.1q1 1.4 2.7 1.7h1.6l.4-.1v-.2l-.2-.3-.9-1c-1-.9-2.6-1.2-3.7-.5M10.2 28.1a4 4 0 0 0-2.2.7l-.2.2v.1l.1.3.3.2 1 .5h.1q.6.2 1.2.2l1-.1h.1l1.2-.6v-.2h-.1v-.1l-.5-.4q-1-.8-2-.8M8.4 10.7a3 3 0 0 0-1 1.5v.3c.4.3 1.4.1 2 0a3 3 0 0 0 1.7-1.3q.4-.4.6-1V10c-1-.4-2.5 0-3.3.7M3.2 13.3v.2q0 1 .4 1.7v.1l.7 1q.5.4 1.1.6l1 .3h.1v-.4a3 3 0 0 0-1.1-2.6q-.7-.6-1.6-.8h-.2q-.2 0-.4 0M12.3 20.9H12v.1l-.6.3a4 4 0 0 0-1.3 2.3v.2q-.1 1 .4 1.8h.2l1-.9.3-.5.1-.1.1-.2.2-.5.1-1.1c0-1.1 0-1.1-.2-1.4zM11 13.6a4 4 0 0 0-2.6 1h-.1L8 15l-.5 1v.3l-.1.1v.7q.7.1 1.2-.2h.2q.9-.4 1.5-1.3L11 14zM11 17.2l-.3.1q-1.5.6-2 2l-.3 1v.2c0 1 0 1 .2 1H9a4 4 0 0 0 1.6-1.4q.6-1 .6-2v-.2zM10.4 4V4h-.1v.6z"/><path d="M15 25.6v.7h.1v-.7M7 6.6v.2h.1zM10.2 24.4v.4zM7.5 20.8v.2z"/><path d="M15 25.6v.2h.1z"/></g>
      {/* Static: just the five filled stars. Animated: empty stars underneath so each one
          reads as filling in rather than popping out of blank space. */}
      {animated &&
        STAR_PATHS.map((d, i) => (
          <path key={`empty-${i}`} d={d} fill="#111" opacity={0.15} />
        ))}
      {STAR_PATHS.map((d, i) =>
        animated ? (
          <motion.path
            key={`star-${i}`}
            d={d}
            fill="#111"
            // fill-box so each star scales about its own centre, not the artboard origin.
            style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
            variants={{
              hidden: { opacity: 0, scale: 0.3 },
              shown: {
                opacity: 1,
                scale: 1,
                transition: { type: 'spring', stiffness: 380, damping: 20, delay: 0.2 + i * 0.15 },
              },
            }}
          />
        ) : (
          <path key={`filled-${i}`} d={d} fill="#111" />
        ),
      )}
      {/* "4.9" */}
      <path fill="#111" d="M119.9 19.5q.6 0 1 .4t.4 1-.4 1q-.4.3-1 .4-.7 0-1-.4-.5-.4-.5-1t.4-1q.5-.4 1-.4m6.7-9.7q2.5 0 3.4 1.5 1 1.5 1 4.5 0 2.1-.4 3.5-.3 1.5-1.3 2.1a4 4 0 0 1-2.5.8q-1.2 0-2-.5-1-.5-1.4-1.3l-.5-1.7h2.1q.2.8.6 1.2.6.3 1.3.3 1 0 1.5-.8t.5-2.9q-.3.6-1 .8t-1.6.3q-1 0-2-.4-.8-.4-1.3-1.3t-.5-2.1q0-1.9 1.1-3 1.2-1 3-1m-10.4 8h1.5v2h-1.5V22H114v-2.3H108V18l5.5-7.8h2.7zm-5.6 0h3.4v-5.1zm16.1-6q-.8 0-1.4.5a2 2 0 0 0-.5 1.5q0 .8.5 1.4t1.5.5q.8 0 1.4-.5t.5-1.4q0-1-.5-1.5t-1.5-.6" />
      <g fill="#111" clipPath="url(#mb-laurel-r)"><path d="M149.2 18.7h.4v.2l-.1.4-.3.6a4 4 0 0 1-2.3 2h-1.6v-.4l.3-.7v-.2l.2-.2q.8-1.2 2.2-1.6zM139.7.5l.3.1.6.3a4 4 0 0 1 2 2.4V5l-.5-.1-.6-.3-.8-.6-.4-.5-.5-.8v-.1l-.2-.7-.1-1V.4zM138.6 23.8h.2v.1l.7.7.5 1q.3.6.2 1.2a3 3 0 0 1-.4 1.8l-.4.4h-.1l-.1-.1a4 4 0 0 1-1.2-2.1q0-1.2.2-2.2l.1-.4V24zM148.8 7.2v.4l.1.9v1l-.4 1-.5.8q-.6.7-1.2 1h-.1l-.3-.9V9.8a4 4 0 0 1 1.1-1.9l.4-.3.5-.4q.2-.2.4 0M145.8 2.4l.2.2v.2q.5.7.5 1.6v1.1q-.3 1.3-1.3 2.2l-.2.2-.2-.2v-.1a4 4 0 0 1-.4-2.8 4 4 0 0 1 1.1-2.1zM140.3 6h.9q1 .1 1.8.8V7q.7.7 1 1.5v.1l-.3.1q-1.6.2-3-.6a5 5 0 0 1-1.3-1.6V6h.9M148 24v.3l-.1.1v.1q-1 1.4-2.7 1.7h-1.6l-.4-.1v-.2l.3-.4.8-1c1-.8 2.6-1 3.8-.4M142.8 28.1l.7.1a4 4 0 0 1 1.7.8v.1l-.1.3-.3.2-.6.3-.5.2q-.6.2-1.2.2l-1-.1h-.1l-1.2-.6v-.2h.1v-.1h.1l.4-.4q1-.8 2-.8M144.6 10.7a3 3 0 0 1 1 1.5v.3c-.4.3-1.4.1-2 0a3 3 0 0 1-1.7-1.3q-.4-.4-.6-1V10c1-.4 2.5 0 3.3.7M149.8 13.3v.2q0 1-.4 1.7v.1l-.7.9q-.5.5-1.1.7l-1 .3h-.1V16q.3-1.2 1.1-1.9.7-.6 1.6-.8h.2q.2 0 .4 0M140.7 20.9h.2v.1l.6.3a4 4 0 0 1 1.3 2.3v.2q.1 1-.4 1.8h-.2l-1-.9-.3-.5-.1-.1-.1-.2-.2-.5-.1-1.1c0-1.1 0-1.1.2-1.4zM142 13.6a4 4 0 0 1 2.7 1l.3.3.4.8v.1l.2.4v.8q-.7.1-1.2-.2h-.2q-.9-.4-1.5-1.3-.5-.7-.7-1.5zM142 17.2l.4.1q1.4.6 2 2l.2 1v.2c0 1 0 1-.2 1h-.4a4 4 0 0 1-1.6-1.4q-.6-1-.6-2v-.2zM142.6 4V4h.1v.6z"/><path d="M138 25.6v.7l-.1-.3v-.4M146 6.6v.2h-.1zM142.8 24.4v.4zM145.6 20.8v.2h-.1zM138 25.6v.1z"/></g>
      <defs><clipPath id="mb-laurel-l"><path fill="#fff" d="M18 0H0v32h18z"/></clipPath><clipPath id="mb-laurel-r"><path fill="#fff" d="M135 0h18v32h-18z"/></clipPath></defs>
    </motion.svg>
  );
}
